import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  ArrowLeft, Search, RefreshCw, Download, Eye, X, FileText, Trash2, 
  CheckSquare, Plane, Bus, Filter, ArrowUpDown, ChevronUp, ChevronDown,
  Users, Calendar, CreditCard, RotateCw, MoreVertical
} from 'lucide-react';
import axios from 'axios';
import ReservationOpsModal from '../../components/ReservationOpsModal';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../components/ui/dropdown-menu';
import { translatePaymentStatus } from '../../utils/statusLabels';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  'pending': { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800' },
  'confirmed': { label: 'Confirmada', className: 'bg-green-100 text-green-800' },
  'paid': { label: 'Paga', className: 'bg-green-100 text-green-800' },
  'cancelled': { label: 'Cancelada', className: 'bg-red-100 text-red-800' },
  'cancelled_by_agency': { label: 'Cancelada pela agência', className: 'bg-red-100 text-red-800' },
  'cancelled_by_customer': { label: 'Cancelada pelo cliente', className: 'bg-red-100 text-red-800' },
  'cancelled_transferred_to_credit': { label: 'Transferido', className: 'bg-teal-100 text-teal-800' },
  'transferred_to_credit': { label: 'Transferido', className: 'bg-teal-100 text-teal-800' },
  'cancellation_requested': { label: 'Cancelamento solicitado', className: 'bg-orange-100 text-orange-800' },
  'awaiting_contract_acceptance': { label: 'Aguardando aceite do contrato', className: 'bg-amber-100 text-amber-800' },
  'awaiting_agency_review': { label: 'Aguardando análise da agência', className: 'bg-orange-100 text-orange-800' },
  'completed': { label: 'Concluída', className: 'bg-blue-100 text-blue-800' },
  'pago_total': { label: 'Pago Total', className: 'bg-green-100 text-green-800' },
  'pago_parcial': { label: 'Pago Parcial', className: 'bg-blue-100 text-blue-800' },
  'pendente': { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800' },
  'atrasado': { label: 'Atrasado', className: 'bg-red-100 text-red-800' },
  'cancelado': { label: 'Cancelado', className: 'bg-gray-100 text-gray-800' },
  'estornado': { label: 'Estornado', className: 'bg-red-100 text-red-700' },
  'trocado': { label: 'Trocado de Pacote', className: 'bg-indigo-100 text-indigo-800' }
};

const CATEGORY_LABELS = {
  'nacional-aereo': 'Nacional Aéreo',
  'nacional-rodoviario': 'Nacional Rodoviário',
  'internacional': 'Internacional',
  'passeio-1-dia': 'Passeio 1 Dia'
};

const AdminReservations = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [contractModal, setContractModal] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showFilters, setShowFilters] = useState(false);
  
  // Modal para associar pagamentos ASAAS
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalReservation, setPaymentModalReservation] = useState(null);
  const [asaasPayments, setAsaasPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [associatingPayment, setAssociatingPayment] = useState(null);
  
  // Modal para trocar pacote
  const [showTransferModal, setShowTransferModal] = useState(false);
  // Ops modal (cancelamento / transferências)
  const [opsModal, setOpsModal] = useState({ open: false, mode: null, reservation: null });
  const [transferReservation, setTransferReservation] = useState(null);
  const [packages, setPackages] = useState([]);
  const [transferForm, setTransferForm] = useState({
    new_package_id: '',
    new_date_id: '',
    new_room_type: '',
    notes: ''
  });
  const [transferring, setTransferring] = useState(false);
  
  // Filters
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');
  const [filterPackage, setFilterPackage] = useState('all');
  const [filterTripPeriod, setFilterTripPeriod] = useState('all'); // 'all', 'upcoming', 'past'
  
  // Sorting
  const [sortField, setSortField] = useState('selected_date');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadReservations();
  }, [navigate]);

  const loadReservations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/reservations/`);
      const reservationsData = response.data;
      
      // Buscar informações de parcelas para cada reserva
      const reservationsWithInstallments = await Promise.all(
        reservationsData.map(async (res) => {
          try {
            // Só buscar parcelas se tiver payment_id ou for boleto
            if (res.payment_method === 'boleto' || res.payment_id) {
              const instResponse = await axios.get(`${BACKEND_URL}/api/asaas/installments/${res.id}`);
              return {
                ...res,
                installments_info: instResponse.data
              };
            }
          } catch (e) {
            // Ignora erro - reserva pode não ter parcelas
          }
          return res;
        })
      );
      
      setReservations(reservationsWithInstallments);
    } catch (error) {
      console.error('Erro ao carregar reservas:', error);
      setReservations([]);
    } finally {
      setLoading(false);
    }
  };

  const [syncing, setSyncing] = useState(null);
  
  const handleSyncPayment = async (reservation) => {
    // O endpoint aceita payment_id, asaas_payment_id ou reservation_id
    const paymentId = reservation.payment_id || reservation.asaas_payment_id || reservation.id;
    
    setSyncing(reservation.id);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/asaas/sync/${paymentId}`);
      
      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: `Pagamento sincronizado! Status: ${response.data.old_status} → ${response.data.new_status}` 
        });
        loadReservations(); // Recarregar lista
      }
    } catch (error) {
      console.error('Erro ao sincronizar:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Erro ao sincronizar com ASAAS. Verifique se esta reserva tem um pagamento associado.' 
      });
    } finally {
      setSyncing(null);
    }
  };

  const [applyingDiscount, setApplyingDiscount] = useState(null);
  
  const handleApplyPixDiscount = async (reservation) => {
    if (reservation.pix_discount_applied) {
      setMessage({ type: 'info', text: 'Desconto PIX já aplicado nesta reserva' });
      return;
    }
    
    if (!window.confirm(`Aplicar desconto PIX de 5% na reserva de ${reservation.customer_name}?`)) {
      return;
    }
    
    setApplyingDiscount(reservation.id);
    
    try {
      const response = await axios.put(`${BACKEND_URL}/api/reservations/${reservation.id}/apply-pix-discount`, {
        pix_discount_applied: true,
        pix_discount_percentage: 5,
        recalculate: true
      });
      
      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: `Desconto PIX aplicado! Novo valor: ${formatPrice(response.data.final_total)}` 
        });
        loadReservations();
      }
    } catch (error) {
      console.error('Erro ao aplicar desconto:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Erro ao aplicar desconto PIX' 
      });
    } finally {
      setApplyingDiscount(null);
    }
  };

  const [applyingBulkDiscount, setApplyingBulkDiscount] = useState(false);
  
  const handleApplyPixDiscountBulk = async () => {
    // Contar quantas reservas PIX não têm desconto
    // Verificar múltiplos indicadores de pagamento PIX
    const pixWithoutDiscount = reservations.filter(r => 
      (r.payment_method === 'pix' || r.payment_method === 'PIX' || r.pix_qr_code || r.billing_type === 'PIX') && 
      (r.payment_status === 'paid' || r.payment_status === 'approved' || r.payment_status === 'RECEIVED' || r.payment_status === 'CONFIRMED' || r.payment_status === 'confirmed' ||
       r.status === 'confirmed' || r.status === 'completed' || r.status === 'Confirmada' || r.status === 'Concluída') &&
      !r.pix_discount_applied
    );
    
    if (pixWithoutDiscount.length === 0) {
      setMessage({ type: 'info', text: 'Não há reservas PIX pendentes de desconto. Verifique se as reservas estão confirmadas e são pagamentos PIX.' });
      return;
    }
    
    if (!window.confirm(`Aplicar desconto PIX de 5% em ${pixWithoutDiscount.length} reserva(s) PIX paga(s)?`)) {
      return;
    }
    
    setApplyingBulkDiscount(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/reservations/apply-pix-discount-bulk`);
      
      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: `${response.data.message}. Detalhes: ${response.data.details?.map(d => d.customer).join(', ') || 'Nenhum'}`
        });
        loadReservations();
      }
    } catch (error) {
      console.error('Erro ao aplicar descontos em massa:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Erro ao aplicar descontos PIX em massa' 
      });
    } finally {
      setApplyingBulkDiscount(false);
    }
  };

  const [fixingPaymentMethods, setFixingPaymentMethods] = useState(false);
  
  const handleFixPaymentMethods = async () => {
    if (!window.confirm('Corrigir métodos de pagamento em reservas que não têm o método definido?')) {
      return;
    }
    
    setFixingPaymentMethods(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/reservations/fix-payment-methods`);
      
      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: response.data.message
        });
        loadReservations();
      }
    } catch (error) {
      console.error('Erro ao corrigir métodos de pagamento:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Erro ao corrigir métodos de pagamento' 
      });
    } finally {
      setFixingPaymentMethods(false);
    }
  };

  const [syncingInstallments, setSyncingInstallments] = useState(false);
  
  const handleSyncInstallments = async () => {
    if (!window.confirm('Sincronizar informações de parcelas de todas as reservas com pagamentos?')) {
      return;
    }
    
    setSyncingInstallments(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/asaas/sync-installments-info`);
      
      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: response.data.message
        });
        loadReservations();
      }
    } catch (error) {
      console.error('Erro ao sincronizar parcelas:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Erro ao sincronizar parcelas' 
      });
    } finally {
      setSyncingInstallments(false);
    }
  };

  // Carregar pacotes para o modal de transferência
  const loadPackages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/packages/`);
      // Filtrar apenas pacotes com datas futuras
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const filteredPackages = (response.data || []).filter(pkg => {
        return pkg.dates?.some(d => {
          const startDate = new Date(d.start_date);
          return startDate >= today && (d.spots_available > 0 || d.available_spots > 0);
        });
      });
      setPackages(filteredPackages);
    } catch (error) {
      console.error('Erro ao carregar pacotes:', error);
    }
  };

  // Abrir modal de transferência
  const openTransferModal = (reservation) => {
    setTransferReservation(reservation);
    setTransferForm({
      new_package_id: '',
      new_date_id: '',
      notes: ''
    });
    setShowTransferModal(true);
    loadPackages();
  };

  // Executar transferência de pacote
  const handleTransferPackage = async () => {
    if (!transferForm.new_package_id || !transferForm.new_date_id) {
      alert('Selecione o novo pacote e a data');
      return;
    }

    setTransferring(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/reservations/${transferReservation.id}/transfer-package`,
        transferForm
      );
      
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
        setShowTransferModal(false);
        setTransferReservation(null);
        setSelectedReservation(null);
        loadReservations();
      }
    } catch (error) {
      console.error('Erro ao transferir:', error);
      alert(error.response?.data?.detail || 'Erro ao transferir cliente');
    } finally {
      setTransferring(false);
    }
  };


  // Abrir modal para buscar pagamentos do ASAAS
  const openPaymentSearchModal = async (reservation) => {
    setPaymentModalReservation(reservation);
    setShowPaymentModal(true);
    setAsaasPayments([]);
    
    if (reservation.customer_cpf) {
      setLoadingPayments(true);
      try {
        const response = await axios.get(`${BACKEND_URL}/api/asaas/search-customer-payments/${reservation.customer_cpf}`);
        if (response.data.success) {
          setAsaasPayments(response.data.payments);
        } else {
          setMessage({ type: 'error', text: response.data.message || 'Cliente não encontrado no ASAAS' });
        }
      } catch (error) {
        console.error('Erro ao buscar pagamentos:', error);
        setMessage({ type: 'error', text: 'Erro ao buscar pagamentos no ASAAS' });
      } finally {
        setLoadingPayments(false);
      }
    }
  };

  // Associar pagamento a reserva
  const associatePayment = async (asaasPaymentId) => {
    if (!paymentModalReservation) return;
    
    setAssociatingPayment(asaasPaymentId);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/asaas/associate-payment?reservation_id=${paymentModalReservation.id}&asaas_payment_id=${asaasPaymentId}`);
      
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
        setShowPaymentModal(false);
        loadReservations();
      }
    } catch (error) {
      console.error('Erro ao associar pagamento:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erro ao associar pagamento' });
    } finally {
      setAssociatingPayment(null);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price || 0);
  };

  // Calcula o valor base da reserva (sem juros de cartão)
  // Valor exibido = preço do pacote x quantidade + adicional de quarto - descontos (cupom/pix)
  const getDisplayValue = (reservation) => {
    // Se tem valor base salvo, usar ele
    if (reservation.base_total) {
      return reservation.base_total;
    }
    
    // Se o pagamento é por cartão com juros, exibir o valor original (sem juros)
    if (reservation.payment_method === 'cartao' && reservation.card_interest_applied) {
      // Retornar valor sem juros
      return reservation.original_total || reservation.final_total || reservation.total_amount;
    }
    
    // Caso contrário, usar o valor final
    return reservation.final_total || reservation.total_amount;
  };

  const getStatusBadge = (status, reservation = null) => {
    // TRANSFERIDO PARA CRÉDITO: prioridade absoluta (não deixa cair em pago_parcial)
    if (status === 'cancelled_transferred_to_credit' || status === 'transferred_to_credit') {
      return (
        <div className="flex flex-col items-start">
          <Badge className="bg-teal-100 text-teal-800" data-testid="status-badge-transferido">Transferido</Badge>
        </div>
      );
    }

    // ESTORNADO: Verificar primeiro se foi estornado
    if (status === 'estornado') {
      const refunded = reservation?.refunded_installments || 0;
      const total = reservation?.total_installments || 0;
      return (
        <div className="flex flex-col items-start">
          <Badge className="bg-red-100 text-red-700" data-testid="status-badge-estornado">Estornado</Badge>
          {total > 1 && <span className="text-xs text-red-500 font-medium mt-1">{refunded}/{total} estornadas</span>}
        </div>
      );
    }

    // Se o status for explicitamente 'pago_parcial', mostrar badge de pago parcial
    if (status === 'pago_parcial') {
      const paid = reservation?.paid_installments || 0;
      const total = reservation?.total_installments || 1;
      return (
        <div className="flex flex-col items-start">
          <Badge className="bg-yellow-100 text-yellow-800">Pago Parcial</Badge>
          {total > 1 && <span className="text-xs text-green-600 font-medium mt-1">{paid}/{total} parcelas</span>}
        </div>
      );
    }
    
    // Se tem informação de parcelas da reserva (total_installments > 1)
    if (reservation?.total_installments > 1) {
      const paid = reservation.paid_installments || 0;
      const total = reservation.total_installments;
      
      // Pago parcial: algumas parcelas pagas
      if (paid > 0 && paid < total) {
        return (
          <div className="flex flex-col items-start">
            <Badge className="bg-yellow-100 text-yellow-800">Pago Parcial</Badge>
            <span className="text-xs text-green-600 font-medium mt-1">{paid}/{total} parcelas</span>
          </div>
        );
      }
      
      // Pago total: todas parcelas pagas
      if (paid >= total) {
        return (
          <div className="flex flex-col items-start">
            <Badge className="bg-green-100 text-green-800">Confirmada</Badge>
            <span className="text-xs text-green-600 font-medium mt-1">{paid}/{total} parcelas</span>
          </div>
        );
      }
      
      // Pendente: nenhuma parcela paga ainda, mas tem parcelamento
      if (paid === 0) {
        const c = STATUS_CONFIG[status] || { label: status || '-', className: 'bg-gray-100 text-gray-800' };
        return (
          <div className="flex flex-col items-start">
            <Badge className={c.className}>{c.label}</Badge>
            <span className="text-xs text-gray-500 font-medium mt-1">0/{total} parcelas</span>
          </div>
        );
      }
    }
    
    // Também verificar installments_info se existir
    if (reservation?.installments_info && reservation.installments_info.total_installments > 1) {
      const paid = reservation.installments_info.paid_installments || 0;
      const total = reservation.installments_info.total_installments;
      
      if (paid > 0 && paid < total) {
        return (
          <div className="flex flex-col items-start">
            <Badge className="bg-yellow-100 text-yellow-800">Pago Parcial</Badge>
            <span className="text-xs text-green-600 font-medium mt-1">{paid}/{total} parcelas</span>
          </div>
        );
      }
      
      // Pendente com parcelamento
      if (paid === 0) {
        const c = STATUS_CONFIG[status] || { label: status || '-', className: 'bg-gray-100 text-gray-800' };
        return (
          <div className="flex flex-col items-start">
            <Badge className={c.className}>{c.label}</Badge>
            <span className="text-xs text-gray-500 font-medium mt-1">0/{total} parcelas</span>
          </div>
        );
      }
    }
    
    const c = STATUS_CONFIG[status] || { label: status || '-', className: 'bg-gray-100 text-gray-800' };
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const getPaymentMethodLabel = (method, reservation = null) => {
    // Se tem método definido, usar ele
    if (method) {
      const labels = { 'pix': 'PIX', 'PIX': 'PIX', 'boleto': 'Boleto', 'cartao': 'Cartão', 'credit_card': 'Cartão' };
      return labels[method] || method;
    }
    // Se não tem método mas tem indicadores de PIX, mostrar PIX
    if (reservation) {
      if (reservation.pix_qr_code || reservation.billing_type === 'PIX') {
        return 'PIX';
      }
    }
    return '-';
  };

  const getRoomTypeLabel = (type) => {
    const labels = { 'compartilhado': 'Compartilhado', 'casal': 'Casal', 'privativo': 'Privativo' };
    return labels[type] || type || '-';
  };

  // Get unique values for filters
  const uniquePackages = useMemo(() => {
    const packages = [...new Set(reservations.map(r => r.package_name).filter(Boolean))];
    return packages.sort();
  }, [reservations]);

  const uniqueCategories = useMemo(() => {
    const categories = [...new Set(reservations.map(r => r.package_category).filter(Boolean))];
    return categories;
  }, [reservations]);

  // Filter and sort reservations
  const filteredAndSortedReservations = useMemo(() => {
    let filtered = reservations.filter(res => {
      // Text search
      const matchesSearch = !searchTerm || 
        res.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        res.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        res.package_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        res.customer_cpf?.includes(searchTerm);
      
      // Category filter
      const matchesCategory = filterCategory === 'all' || res.package_category === filterCategory;
      
      // Status filter
      const matchesStatus = filterStatus === 'all' || res.status === filterStatus;
      
      // Payment method filter
      const matchesPayment = filterPayment === 'all' || res.payment_method === filterPayment;
      
      // Package filter
      const matchesPackage = filterPackage === 'all' || res.package_name === filterPackage;

      // Trip period filter (upcoming/past/all) — usa selected_date do pacote
      let matchesTripPeriod = true;
      if (filterTripPeriod !== 'all') {
        const rawDate = res.selected_date || '';
        // Aceita 'DD/MM/YYYY' ou 'YYYY-MM-DD' ou 'YYYY-MM-DDTHH:MM:SS'
        let tripDate = null;
        if (rawDate) {
          if (rawDate.includes('/')) {
            const parts = rawDate.split(' ')[0].split('/');
            if (parts.length === 3) tripDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}T00:00:00`);
          } else {
            tripDate = new Date(rawDate.slice(0, 10) + 'T00:00:00');
          }
        }
        const today = new Date(); today.setHours(0, 0, 0, 0);
        if (!tripDate || isNaN(tripDate.getTime())) {
          matchesTripPeriod = filterTripPeriod === 'all';
        } else if (filterTripPeriod === 'upcoming') {
          matchesTripPeriod = tripDate >= today;
        } else if (filterTripPeriod === 'past') {
          matchesTripPeriod = tripDate < today;
        }
      }

      return matchesSearch && matchesCategory && matchesStatus && matchesPayment && matchesPackage && matchesTripPeriod;
    });
    
    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle different types
      if (sortField === 'total_amount' || sortField === 'number_of_people') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortField === 'created_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else if (sortField === 'selected_date') {
        // Parse formatos: "DD/MM/YYYY", "DD a DD/MM/YYYY", "DD de Mês/YYYY", "DD/MM a DD/MM/YYYY" ou ISO
        const MESES = { jan:1,fev:2,mar:3,abr:4,mai:5,jun:6,jul:7,ago:8,set:9,out:10,nov:11,dez:12 };
        const parseTrip = (v) => {
          if (!v) return 0;
          const s = String(v);
          const m = s.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
          if (m) {
            const dt = new Date(parseInt(m[3],10), parseInt(m[2],10) - 1, parseInt(m[1],10));
            return isNaN(dt.getTime()) ? 0 : dt.getTime();
          }
          // "DD de Mês/YYYY" ou "DD a DD de Mês/YYYY"
          const m2 = s.match(/(\d{1,2})[^\d]+de\s+([A-Za-zçãéô]+)\s*\/\s*(\d{4})/i);
          if (m2) {
            const key = m2[2].slice(0,3).toLowerCase().replace('ç','c');
            const mo = MESES[key];
            if (mo) {
              const dt = new Date(parseInt(m2[3],10), mo - 1, parseInt(m2[1],10));
              return isNaN(dt.getTime()) ? 0 : dt.getTime();
            }
          }
          const iso = new Date(s.slice(0, 10) + 'T00:00:00');
          return isNaN(iso.getTime()) ? 0 : iso.getTime();
        };
        aVal = parseTrip(aVal);
        bVal = parseTrip(bVal);
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
    
    return filtered;
  }, [reservations, searchTerm, filterCategory, filterStatus, filterPayment, filterPackage, filterTripPeriod, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  // Selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredAndSortedReservations.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredAndSortedReservations.map(r => r.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Excluir ${selectedIds.length} reserva(s)?`)) return;
    
    try {
      await axios.post(`${BACKEND_URL}/api/reservations/bulk-delete`, { ids: selectedIds });
      setMessage({ type: 'success', text: `${selectedIds.length} reserva(s) excluída(s)!` });
      setSelectedIds([]);
      loadReservations();
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao excluir' });
    }
  };

  const viewContract = async (reservationId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contracts/reservation/${reservationId}`);
      setContractModal(response.data);
    } catch (error) {
      alert('Contrato não encontrado.');
    }
  };

  const downloadContract = (contract) => {
    const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Contrato</title><style>body{font-family:Arial;padding:40px;max-width:800px;margin:0 auto;}</style></head><body>${contract.content}</body></html>`;
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Contrato.html';
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setFilterCategory('all');
    setFilterStatus('all');
    setFilterPayment('all');
    setFilterPackage('all');
    setSearchTerm('');
  };

  const activeFiltersCount = [filterCategory, filterStatus, filterPayment, filterPackage].filter(f => f !== 'all').length;

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/admin')} className="flex items-center space-x-2 hover:opacity-80">
              <ArrowLeft className="w-5 h-5" />
              <span>Voltar</span>
            </button>
            <div className="h-6 w-px bg-white/30"></div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Users className="w-6 h-6" />
              Reservas / Clientes
            </h1>
          </div>
          <div className="flex items-center space-x-2 flex-wrap gap-2">
            {selectedIds.length > 0 && (
              <Button onClick={handleBulkDelete} variant="outline" size="sm" className="text-red-400 border-red-400 hover:bg-red-500 hover:text-white">
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir ({selectedIds.length})
              </Button>
            )}
            <Button 
              onClick={handleFixPaymentMethods} 
              disabled={fixingPaymentMethods}
              variant="outline" 
              size="sm" 
              className="text-yellow-300 border-yellow-300 hover:bg-yellow-500 hover:text-white"
              title="Corrigir métodos de pagamento em reservas que não têm o método definido"
            >
              {fixingPaymentMethods ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              Corrigir Métodos
            </Button>
            <Button 
              onClick={handleSyncInstallments} 
              disabled={syncingInstallments}
              variant="outline" 
              size="sm" 
              className="text-purple-300 border-purple-300 hover:bg-purple-500 hover:text-white"
              title="Sincronizar informações de parcelas (0/X) de todas as reservas"
            >
              {syncingInstallments ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sincronizar Parcelas
            </Button>
            <Button 
              onClick={handleApplyPixDiscountBulk} 
              disabled={applyingBulkDiscount}
              variant="outline" 
              size="sm" 
              className="text-green-300 border-green-300 hover:bg-green-500 hover:text-white"
              title="Aplicar desconto PIX de 5% em todas as reservas PIX pagas que ainda não têm desconto"
            >
              {applyingBulkDiscount ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              PIX -5% em Massa
            </Button>
            <Button onClick={loadReservations} variant="outline" size="sm" className="text-white border-white hover:bg-white hover:text-[#003366]">
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {message.text && (
          <div className={`mb-4 p-3 rounded-lg flex items-center justify-between ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            <span>{message.text}</span>
            <button onClick={() => setMessage({ type: '', text: '' })}><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Search and Filters */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nome, email, CPF ou pacote..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)}
                className={activeFiltersCount > 0 ? 'border-blue-500 text-blue-600' : ''}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtros {activeFiltersCount > 0 && `(${activeFiltersCount})`}
              </Button>
              <Select value={filterTripPeriod} onValueChange={setFilterTripPeriod}>
                <SelectTrigger className="w-full md:w-52" data-testid="filter-trip-period">
                  <SelectValue placeholder="Período da Viagem" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Viagens</SelectItem>
                  <SelectItem value="upcoming">Viagens que Acontecerão</SelectItem>
                  <SelectItem value="past">Viagens que Já Aconteceram</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {showFilters && (
              <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Tipo de Viagem</label>
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {uniqueCategories.map(cat => (
                        <SelectItem key={cat} value={cat}>{CATEGORY_LABELS[cat] || cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Status</label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="pending">Pendente</SelectItem>
                      <SelectItem value="confirmed">Confirmada</SelectItem>
                      <SelectItem value="cancelled">Cancelada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Pagamento</label>
                  <Select value={filterPayment} onValueChange={setFilterPayment}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="pix">PIX</SelectItem>
                      <SelectItem value="boleto">Boleto</SelectItem>
                      <SelectItem value="credit_card">Cartão</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Destino</label>
                  <Select value={filterPackage} onValueChange={setFilterPackage}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {uniquePackages.map(pkg => (
                        <SelectItem key={pkg} value={pkg}>{pkg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {activeFiltersCount > 0 && (
                  <div className="col-span-2 md:col-span-4">
                    <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-600">
                      <X className="w-3 h-3 mr-1" /> Limpar Filtros
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="flex gap-3 text-sm mb-4 flex-wrap">
          <span className="bg-gray-100 px-3 py-1 rounded">Total: <strong>{filteredAndSortedReservations.length}</strong></span>
          <span className="bg-yellow-100 px-3 py-1 rounded text-yellow-800">
            Pendentes: <strong>{filteredAndSortedReservations.filter(r => r.status === 'pending').length}</strong>
          </span>
          <span className="bg-green-100 px-3 py-1 rounded text-green-800">
            Confirmadas: <strong>{filteredAndSortedReservations.filter(r => r.status === 'confirmed').length}</strong>
          </span>
          {filteredAndSortedReservations.filter(r => r.status === 'estornado').length > 0 && (
            <span className="bg-red-100 px-3 py-1 rounded text-red-700">
              Estornadas: <strong>{filteredAndSortedReservations.filter(r => r.status === 'estornado').length}</strong>
            </span>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#003366]"></div>
          </div>
        ) : filteredAndSortedReservations.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-gray-500">Nenhuma reserva encontrada</CardContent></Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-3 text-left w-10">
                      <Checkbox
                        checked={selectedIds.length === filteredAndSortedReservations.length && filteredAndSortedReservations.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th className="p-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('customer_name')}>
                      <div className="flex items-center gap-1">Cliente <SortIcon field="customer_name" /></div>
                    </th>
                    <th className="p-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('package_name')}>
                      <div className="flex items-center gap-1">Pacote <SortIcon field="package_name" /></div>
                    </th>
                    <th className="p-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('selected_date')}>
                      <div className="flex items-center gap-1">Data <SortIcon field="selected_date" /></div>
                    </th>
                    <th className="p-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('total_amount')}>
                      <div className="flex items-center gap-1">Valor <SortIcon field="total_amount" /></div>
                    </th>
                    <th className="p-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')}>
                      <div className="flex items-center gap-1">Status <SortIcon field="status" /></div>
                    </th>
                    <th className="p-3 text-left">Pagamento</th>
                    <th className="p-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('created_at')}>
                      <div className="flex items-center gap-1">Data Aquisição <SortIcon field="created_at" /></div>
                    </th>
                    <th className="p-3 text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedReservations.map((res) => (
                    <tr key={res.id} className={`border-b hover:bg-gray-50 ${selectedIds.includes(res.id) ? 'bg-blue-50' : ''}`}>
                      <td className="p-3">
                        <Checkbox
                          checked={selectedIds.includes(res.id)}
                          onCheckedChange={() => toggleSelect(res.id)}
                        />
                      </td>
                      <td className="p-3">
                        <div className="font-medium">{res.customer_name}</div>
                        <div className="text-xs text-gray-500">{res.customer_cpf}</div>
                      </td>
                      <td className="p-3">
                        {(() => {
                          const isCreditTransfer = res.status === 'cancelled_transferred_to_credit' || res.status === 'transferred_to_credit';
                          const bagKind = res.credit_bag_transfer?.kind || res.credit_bag_kind || res.credit_transfer_kind;
                          const transferredLabel = bagKind === 'limited' ? 'Transferido bolsa limitada' : 'Transferido bolsa de crédito';
                          return (
                            <>
                              <div className={`flex items-center gap-1 ${isCreditTransfer ? 'line-through text-gray-400' : ''}`}>
                                {(res.package_category?.toLowerCase().includes('aereo') || res.package_category?.toLowerCase().includes('aéreo') || res.package_category === 'internacional') ? (
                                  <Plane className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <Bus className="w-4 h-4 text-green-600" />
                                )}
                                <span>{res.package_name}</span>
                              </div>
                              {isCreditTransfer ? (
                                <div className="text-xs text-teal-700 font-semibold mt-0.5" data-testid={`transferred-label-${res.id}`}>
                                  {transferredLabel}
                                </div>
                              ) : (
                                <div className="text-xs text-gray-500">
                                  {res.number_of_people || 1} pessoa(s)
                                  {res.transport_mode === 'without_air' ? (
                                    <span className="ml-1 text-amber-700 font-medium">• Aéreo não contratado</span>
                                  ) : (res.city_departure || res.boarding_point) ? (
                                    <span className="ml-1">• {res.city_departure || res.boarding_point}</span>
                                  ) : null}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td className="p-3 text-gray-600">
                        <span className={res.status === 'cancelled_transferred_to_credit' || res.status === 'transferred_to_credit' ? 'line-through text-gray-400' : ''}>
                          {res.selected_date || '-'}
                        </span>
                      </td>
                      <td className="p-3">
                        {(() => {
                          const isCreditTransfer = res.status === 'cancelled_transferred_to_credit' || res.status === 'transferred_to_credit';
                          const creditValue = Number(res.credit_bag_transfer?.amount || res.credit_transferred_amount || res.credit_bag_amount || 0);
                          if (isCreditTransfer) {
                            return (
                              <div className="font-medium" data-testid={`credit-transferred-value-${res.id}`}>
                                <div className="line-through text-gray-400 text-xs">{formatPrice(res.original_total || res.total_amount)}</div>
                                <div className="text-teal-700 font-bold">{formatPrice(creditValue)}</div>
                                <div className="text-[10px] text-teal-600">Valor transferido</div>
                              </div>
                            );
                          }
                          return (
                        <div className="font-medium text-[#003366]">
                          {(res.coupon_code || res.pix_discount_applied) ? (
                            <>
                              <span className="line-through text-gray-400 text-xs block">
                                {formatPrice(res.original_total || res.total_amount)}
                              </span>
                              <span>{formatPrice(getDisplayValue(res))}</span>
                              {res.coupon_code && (
                                <span className="text-xs text-green-600 block">
                                  🎟️ {res.coupon_code} ({res.coupon_discount_value || 10}%)
                                </span>
                              )}
                              {res.pix_discount_applied && (
                                <span className="text-xs text-blue-600 block">
                                  💰 PIX à vista (-5%)
                                </span>
                              )}
                            </>
                          ) : (
                            formatPrice(getDisplayValue(res))
                          )}
                          {/* Mostrar tipo de quarto se diferente de compartilhado */}
                          {res.room_type && res.room_type !== 'shared' && (
                            <span className="text-xs text-purple-600 block">
                              {res.room_type === 'private' ? '🛏️ Privativo' : 
                               res.room_type === 'couple' ? '💑 Casal' : ''}
                            </span>
                          )}
                        </div>
                          );
                        })()}
                      </td>
                      <td className="p-3">
                        <div>{getStatusBadge(res.status, res)}</div>
                      </td>
                      <td className="p-3">
                        <div className="text-gray-700">{getPaymentMethodLabel(res.payment_method, res)}</div>
                        <div className="text-xs text-gray-500">{getRoomTypeLabel(res.room_type)}</div>
                      </td>
                      <td className="p-3">
                        <div className="text-xs text-gray-700" data-testid={`acquired-at-${res.id}`}>
                          {res.created_at
                            ? new Date(res.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : '-'}
                        </div>
                        <div className="text-[10px] text-gray-400">
                          {res.created_at ? new Date(res.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setSelectedReservation(res)} title="Ver detalhes">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => viewContract(res.id)} title="Ver contrato">
                            <FileText className="w-4 h-4" />
                          </Button>
                          {/* Botão para aplicar desconto PIX - aparece para QUALQUER reserva sem desconto PIX */}
                          {!res.pix_discount_applied && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleApplyPixDiscount(res)}
                              disabled={applyingDiscount === res.id}
                              title="Aplicar desconto PIX de 5%"
                              className="text-green-600 hover:text-green-800 hover:bg-green-50"
                            >
                              {applyingDiscount === res.id ? (
                                <RotateCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <span className="text-xs font-bold">-5%</span>
                              )}
                            </Button>
                          )}
                          {/* Botão de sincronização para reservas pendentes */}
                          {(res.status === 'pending' || res.status === 'Pendente' || 
                            res.payment_status === 'PENDING' || res.payment_status === 'pending') && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleSyncPayment(res)}
                              disabled={syncing === res.id}
                              title="Sincronizar status de pagamento com ASAAS"
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <RotateCw className={`w-4 h-4 ${syncing === res.id ? 'animate-spin' : ''}`} />
                            </Button>
                          )}
                          {/* Botão para buscar/associar pagamentos do ASAAS */}
                          {(res.status === 'pending' || res.status === 'Pendente') && res.customer_cpf && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => openPaymentSearchModal(res)}
                              title="Buscar pagamentos no ASAAS pelo CPF"
                              className="text-purple-600 hover:text-purple-800"
                            >
                              <Search className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Menu de Ações — Cancelar / Transferências */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" title="Ações" data-testid={`row-actions-btn-${res.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem data-testid="action-cancel" onClick={() => setOpsModal({ open: true, mode: 'cancel', reservation: res })}>
                                <X className="w-4 h-4 mr-2 text-red-600" /> Cancelar reserva
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem data-testid="action-transfer-date" onClick={() => setOpsModal({ open: true, mode: 'transfer-date', reservation: res })}>
                                <Calendar className="w-4 h-4 mr-2 text-blue-600" /> Transferir para outra data
                              </DropdownMenuItem>
                              <DropdownMenuItem data-testid="action-transfer-package" onClick={() => setOpsModal({ open: true, mode: 'transfer-package', reservation: res })}>
                                <RefreshCw className="w-4 h-4 mr-2 text-indigo-600" /> Transferir para outro pacote
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem data-testid="action-transfer-credit-normal" onClick={() => setOpsModal({ open: true, mode: 'transfer-credit-normal', reservation: res })}>
                                <CreditCard className="w-4 h-4 mr-2 text-emerald-600" /> Transferir para Bolsa de Crédito
                              </DropdownMenuItem>
                              <DropdownMenuItem data-testid="action-transfer-credit-limited" onClick={() => setOpsModal({ open: true, mode: 'transfer-credit-limited', reservation: res })}>
                                <CreditCard className="w-4 h-4 mr-2 text-amber-600" /> Transferir para Bolsa Limitada
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Modal de Ações (cancelar / transferências) */}
      <ReservationOpsModal
        open={opsModal.open}
        mode={opsModal.mode}
        reservation={opsModal.reservation}
        onOpenChange={(v) => setOpsModal(s => ({ ...s, open: v }))}
        onDone={() => { loadReservations(); }}
      />

      {/* Detail Modal */}
      <Dialog open={!!selectedReservation} onOpenChange={() => setSelectedReservation(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes da Reserva</DialogTitle>
          </DialogHeader>
          {selectedReservation && (
            <div className="space-y-4">
              {/* Informações da Reserva */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div><label className="text-xs text-gray-500">Pacote</label><p className="font-medium">{selectedReservation.package_name}</p></div>
                <div><label className="text-xs text-gray-500">Data</label><p>{selectedReservation.selected_date || '-'}</p></div>
                <div>
                  <label className="text-xs text-gray-500">Valor Total</label>
                  {(selectedReservation.coupon_code || selectedReservation.pix_discount_applied) ? (
                    <div>
                      <p className="line-through text-gray-400 text-sm">{formatPrice(selectedReservation.original_total || selectedReservation.total_amount)}</p>
                      <p className="font-medium text-[#003366]">{formatPrice(selectedReservation.final_total || selectedReservation.total_amount)}</p>
                      {selectedReservation.coupon_code && (
                        <p className="text-xs text-green-600">
                          🎟️ Cupom: {selectedReservation.coupon_code} ({selectedReservation.coupon_discount_value}%)
                          {selectedReservation.coupon_discount_amount && ` = -${formatPrice(selectedReservation.coupon_discount_amount)}`}
                        </p>
                      )}
                      {selectedReservation.pix_discount_applied && (
                        <p className="text-xs text-blue-600">
                          💰 Desconto PIX à vista: -5%
                          {selectedReservation.pix_discount_amount && ` = -${formatPrice(selectedReservation.pix_discount_amount)}`}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="font-medium text-[#003366]">{formatPrice(selectedReservation.total_amount)}</p>
                  )}
                </div>
                <div><label className="text-xs text-gray-500">Status</label>{getStatusBadge(selectedReservation.status)}</div>
                
                {/* Informações de Pagamento */}
                <div className="col-span-2 bg-blue-50 rounded-lg p-3">
                  <label className="text-xs text-gray-500 font-semibold">Informações de Pagamento</label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div>
                      <p className="text-xs text-gray-500">Método</p>
                      <p className="font-medium text-sm">
                        {selectedReservation.payment_method === 'pix' || selectedReservation.billing_type === 'PIX' ? '💳 PIX' :
                         selectedReservation.payment_method === 'boleto' || selectedReservation.billing_type === 'BOLETO' ? '📄 Boleto' :
                         selectedReservation.payment_method === 'cartao' || selectedReservation.billing_type === 'CREDIT_CARD' ? '💳 Cartão' :
                         selectedReservation.payment_method || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Parcelas</p>
                      <p className="font-medium text-sm">
                        {selectedReservation.total_installments || selectedReservation.installment_count || 1}x
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Valor/Parcela</p>
                      <p className="font-medium text-sm">
                        {formatPrice((selectedReservation.final_total || selectedReservation.total_amount) / (selectedReservation.total_installments || selectedReservation.installment_count || 1))}
                      </p>
                    </div>
                  </div>
                  {/* Datas de Vencimento */}
                  {selectedReservation.installment_due_dates && selectedReservation.installment_due_dates.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-blue-200">
                      <p className="text-xs text-gray-500 mb-1">Datas de Vencimento</p>
                      <div className="flex flex-wrap gap-1">
                        {selectedReservation.installment_due_dates.map((date, idx) => (
                          <span key={idx} className="text-xs bg-white px-2 py-1 rounded border">
                            {idx + 1}ª: {new Date(date).toLocaleDateString('pt-BR')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Botão de Sincronizar Pagamento */}
                {(selectedReservation.payment_id || selectedReservation.asaas_payment_id) && selectedReservation.status !== 'confirmed' && (
                  <div className="col-span-2">
                    <Button 
                      size="sm"
                      onClick={() => handleSyncPayment(selectedReservation)}
                      disabled={syncing === selectedReservation.id}
                      className="bg-blue-600 hover:bg-blue-700 w-full"
                    >
                      <RotateCw className={`w-4 h-4 mr-2 ${syncing === selectedReservation.id ? 'animate-spin' : ''}`} />
                      {syncing === selectedReservation.id ? 'Sincronizando...' : 'Sincronizar Status ASAAS'}
                    </Button>
                  </div>
                )}
                
                {/* Botão para Aplicar Desconto PIX */}
                {(selectedReservation.payment_method === 'pix' || 
                  selectedReservation.payment_method === 'PIX' || 
                  selectedReservation.pix_qr_code || 
                  selectedReservation.billing_type === 'PIX') && 
                 !selectedReservation.pix_discount_applied && (
                  <div className="col-span-2">
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => handleApplyPixDiscount(selectedReservation)}
                      disabled={applyingDiscount === selectedReservation.id}
                      className="border-green-500 text-green-600 hover:bg-green-50 w-full"
                    >
                      {applyingDiscount === selectedReservation.id ? (
                        <>
                          <RotateCw className="w-4 h-4 mr-2 animate-spin" />
                          Aplicando...
                        </>
                      ) : (
                        <>
                          💰 Aplicar Desconto PIX (-5%)
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                {/* Botão Trocar Pacote */}
                <div className="col-span-2">
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => openTransferModal(selectedReservation)}
                    className="border-indigo-500 text-indigo-600 hover:bg-indigo-50 w-full"
                  >
                    🔄 Trocar Pacote
                  </Button>
                </div>
                
                {/* Histórico de Transferências */}
                {selectedReservation.transfer_history && selectedReservation.transfer_history.length > 0 && (
                  <div className="col-span-2 bg-indigo-50 p-3 rounded-lg">
                    <label className="text-xs font-semibold text-indigo-800">Histórico de Transferências</label>
                    <div className="space-y-2 mt-2">
                      {selectedReservation.transfer_history.map((t, idx) => (
                        <div key={idx} className="text-xs bg-white p-2 rounded border border-indigo-200">
                          <p><strong>{new Date(t.transfer_date).toLocaleDateString('pt-BR')}</strong></p>
                          <p>De: {t.from_package_name} ({t.from_selected_date?.slice(0,10)})</p>
                          <p>Para: {t.to_package_name} ({t.to_selected_date?.slice(0,10)})</p>
                          {t.notes && <p className="text-gray-600 italic">{t.notes}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="text-xs text-gray-500">
                    {selectedReservation.transport_mode === 'without_air' ? 'Transporte' :
                      (selectedReservation.package_category?.toLowerCase().includes('aereo') || selectedReservation.package_category?.toLowerCase().includes('aéreo') || selectedReservation.package_category === 'internacional') 
                        ? 'Cidade de Embarque' 
                        : 'Ponto de Embarque'}
                  </label>
                  <p className={selectedReservation.transport_mode === 'without_air' ? 'text-amber-700 font-medium' : ''}>
                    {selectedReservation.transport_mode === 'without_air'
                      ? 'Aéreo não contratado'
                      : (selectedReservation.city_departure || selectedReservation.boarding_point || '-')}
                  </p>
                </div>
                <div><label className="text-xs text-gray-500">Qtd. Pessoas</label><p>{selectedReservation.number_of_people || 1}</p></div>
              </div>

              {/* Viajantes */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Viajantes ({selectedReservation.travelers?.length || 1})
                </h4>
                
                {selectedReservation.travelers && selectedReservation.travelers.length > 0 ? (
                  <div className="space-y-3">
                    {selectedReservation.travelers.map((traveler, index) => (
                      <div key={index} className={`p-3 rounded-lg ${traveler.is_main ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-sm">
                            {traveler.is_main ? '👤 Responsável' : `Viajante ${index + 1}`}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                          <div>
                            <label className="text-xs text-gray-500">Nome Completo</label>
                            <p className="font-medium">{traveler.name || '-'}</p>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">CPF</label>
                            <p>{traveler.cpf ? traveler.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : '-'}</p>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Data de Nascimento</label>
                            <p>{traveler.birth_date || '-'}</p>
                          </div>
                          {traveler.email && (
                            <div>
                              <label className="text-xs text-gray-500">Email</label>
                              <p>{traveler.email}</p>
                            </div>
                          )}
                          {traveler.phone && (
                            <div>
                              <label className="text-xs text-gray-500">Telefone</label>
                              <p>{traveler.phone}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Fallback para reservas antigas sem array de viajantes
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-sm">👤 Responsável</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                      <div>
                        <label className="text-xs text-gray-500">Nome Completo</label>
                        <p className="font-medium">{selectedReservation.customer_name || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">CPF</label>
                        <p>{selectedReservation.customer_cpf || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Email</label>
                        <p>{selectedReservation.customer_email || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Telefone</label>
                        <p>{selectedReservation.customer_phone || '-'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Contract Modal */}
      <Dialog open={!!contractModal} onOpenChange={() => setContractModal(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contrato</DialogTitle>
          </DialogHeader>
          {contractModal && (
            <div>
              <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: contractModal.content }} />
              <div className="mt-4 flex justify-end">
                <Button onClick={() => downloadContract(contractModal)}>
                  <Download className="w-4 h-4 mr-2" />
                  Baixar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal para buscar/associar pagamentos do ASAAS */}
      <Dialog open={showPaymentModal} onOpenChange={() => setShowPaymentModal(false)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Boletos no ASAAS</DialogTitle>
          </DialogHeader>
          {paymentModalReservation && (
            <div>
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="font-medium">{paymentModalReservation.customer_name}</p>
                <p className="text-sm text-gray-600">{paymentModalReservation.package_name}</p>
                <p className="text-sm text-gray-600">Valor Total: {formatPrice(paymentModalReservation.total_amount)}</p>
                <p className="text-sm text-gray-600">CPF: {paymentModalReservation.customer_cpf}</p>
                {paymentModalReservation.total_installments > 0 && (
                  <p className="text-sm font-medium mt-2 text-purple-600">
                    Status atual: {paymentModalReservation.paid_installments || 0}/{paymentModalReservation.total_installments} parcelas pagas
                  </p>
                )}
              </div>
              
              {loadingPayments ? (
                <div className="flex items-center justify-center p-8">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                  <span className="ml-2">Buscando boletos no ASAAS...</span>
                </div>
              ) : asaasPayments.length === 0 ? (
                <div className="text-center p-8 text-gray-500">
                  <p>Nenhum boleto encontrado no ASAAS para este CPF.</p>
                  <p className="text-sm mt-2">Verifique se a cobrança foi criada no ASAAS.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Resumo */}
                  <div className="p-3 bg-purple-50 rounded-lg mb-4">
                    <p className="text-sm font-medium text-purple-800">
                      Total de boletos encontrados: {asaasPayments.length}
                    </p>
                    <p className="text-sm text-purple-600">
                      Pagos: {asaasPayments.filter(p => ['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH'].includes(p.status)).length} | 
                      Pendentes: {asaasPayments.filter(p => p.status === 'PENDING').length} | 
                      Vencidos: {asaasPayments.filter(p => p.status === 'OVERDUE').length}
                    </p>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">
                    Clique em "Associar" para vincular TODOS os boletos do carnê à reserva:
                  </p>
                  
                  {/* Agrupar por carnê (installment_count > 1 com mesma descrição) */}
                  {asaasPayments.map((payment, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 border rounded-lg ${
                        payment.status === 'RECEIVED' || payment.status === 'CONFIRMED' 
                          ? 'bg-green-50 border-green-200' 
                          : payment.status === 'OVERDUE'
                            ? 'bg-red-50 border-red-200'
                            : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{payment.description || 'Pagamento'}</p>
                          <div className="grid grid-cols-2 gap-2 mt-1">
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Valor:</span> {formatPrice(payment.value)}
                            </p>
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Parcela:</span> {payment.installment_number || 1}/{payment.installment_count || 1}
                            </p>
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Vencimento:</span> {payment.due_date ? new Date(payment.due_date).toLocaleDateString('pt-BR') : '-'}
                            </p>
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Pagamento:</span> {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('pt-BR') : '-'}
                            </p>
                          </div>
                          <div className="mt-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              payment.status === 'RECEIVED' || payment.status === 'CONFIRMED' 
                                ? 'bg-green-100 text-green-800' 
                                : payment.status === 'PENDING' 
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : payment.status === 'OVERDUE'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                            }`}>
                              {payment.status === 'RECEIVED' || payment.status === 'CONFIRMED' ? '✅ PAGO' : 
                               payment.status === 'PENDING' ? '⏳ PENDENTE' : 
                               payment.status === 'OVERDUE' ? '⚠️ VENCIDO' : translatePaymentStatus(payment.status)}
                            </span>
                            <span className="ml-2 text-xs text-gray-500">{payment.billing_type}</span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => associatePayment(payment.asaas_id)}
                          disabled={associatingPayment === payment.asaas_id}
                          className="bg-purple-600 hover:bg-purple-700 ml-2"
                        >
                          {associatingPayment === payment.asaas_id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            'Associar'
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  <p className="text-xs text-gray-500 mt-4">
                    💡 Ao associar um boleto de um carnê, TODOS os boletos do mesmo carnê serão vinculados automaticamente.
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal para Trocar Pacote */}
      <Dialog open={showTransferModal} onOpenChange={() => setShowTransferModal(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>🔄 Trocar Pacote</DialogTitle>
          </DialogHeader>
          {transferReservation && (
            <div className="space-y-4">
              {/* Info atual */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{transferReservation.customer_name}</p>
                <p className="text-sm text-gray-600">
                  Pacote atual: {transferReservation.package_name}
                </p>
                <p className="text-sm text-gray-600">
                  Data: {transferReservation.selected_date?.slice(0, 10)}
                </p>
                <p className="text-sm text-gray-600">
                  Pessoas: {transferReservation.number_of_people || 1} — TODOS serão transferidos
                </p>
              </div>

              {/* Lista completa de passageiros */}
              {(transferReservation.travelers || transferReservation.all_travelers || []).length > 0 && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="legacy-transfer-travelers-list">
                  <p className="text-sm font-semibold text-blue-900 mb-2">
                    Passageiros a transferir ({(transferReservation.travelers || transferReservation.all_travelers || []).length}):
                  </p>
                  <ul className="space-y-1">
                    {(transferReservation.travelers || transferReservation.all_travelers || []).map((t, i) => (
                      <li key={i} className="text-xs text-blue-800">
                        <strong>{i === 0 ? 'Titular' : `Acompanhante ${i}`}:</strong> {t.full_name || t.fullName || t.name || '(sem nome)'} — CPF {t.cpf || '—'}
                        {t.birth_date && ` — Nasc. ${t.birth_date}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Selecionar novo pacote */}
              <div>
                <Label className="text-sm font-medium">Novo Pacote *</Label>
                <Select
                  value={transferForm.new_package_id}
                  onValueChange={(val) => setTransferForm({ ...transferForm, new_package_id: val, new_date_id: '' })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o pacote" />
                  </SelectTrigger>
                  <SelectContent>
                    {packages.map(pkg => (
                      <SelectItem key={pkg.id} value={pkg.id}>
                        {pkg.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Selecionar nova data */}
              {transferForm.new_package_id && (
                <div>
                  <Label className="text-sm font-medium">Nova Data *</Label>
                  <Select
                    value={transferForm.new_date_id}
                    onValueChange={(val) => setTransferForm({ ...transferForm, new_date_id: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a data" />
                    </SelectTrigger>
                    <SelectContent>
                      {packages
                        .find(p => p.id === transferForm.new_package_id)
                        ?.dates
                        ?.filter(d => {
                          const startDate = new Date(d.start_date);
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return startDate >= today && ((d.spots_available || d.available_spots || 0) >= (transferReservation.number_of_people || 1));
                        })
                        .map(d => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.start_date?.slice(0, 10)} - {d.spots_available || d.available_spots} vagas
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Observações */}
              <div>
                <Label className="text-sm font-medium">Observações</Label>
                <Input
                  value={transferForm.notes}
                  onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
                  placeholder="Motivo da troca (opcional)"
                />
              </div>

              {/* Info */}
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                <p>📌 O vínculo financeiro será mantido (sem gerar estorno)</p>
                <p>📌 O histórico de transferência ficará registrado</p>
              </div>

              {/* Botões */}
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowTransferModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleTransferPackage}
                  disabled={transferring || !transferForm.new_package_id || !transferForm.new_date_id}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {transferring ? (
                    <>
                      <RotateCw className="w-4 h-4 mr-2 animate-spin" />
                      Transferindo...
                    </>
                  ) : (
                    'Confirmar Transferência'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReservations;
